import React from 'react';

/**
 * Print-ready Invoice View
 * 
 * ARCHITECTURAL NOTE:
 * Sales produces invoice artifacts. Printing exports a snapshot.
 * Finance consumes approved artifacts independently.
 * 
 * This is PRESENTATION layer only.
 * No accounting logic, no Finance logic, no SCM logic.
 */
export default function InvoicePrintView({ invoice }) {
  const totalLineItems = invoice.line_items.reduce((sum, item) => sum + item.amount, 0);
  const totalFees = invoice.fees.reduce((sum, fee) => sum + fee.amount, 0);
  const shippingAmount = typeof invoice.shipping === 'number' ? invoice.shipping : (invoice.shipping?.amount || 0);
  const totalAmount = totalLineItems + totalFees + shippingAmount;

  return (
    <div className="invoice-print-container max-w-4xl mx-auto p-8 bg-white text-black">
      {/* Company Header */}
      <div className="invoice-header flex justify-between items-start pb-6 mb-6 border-b-2 border-gray-800">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Spirit Technologies LLC</h1>
          <div className="text-lg text-gray-600 mb-2">Sales Invoice</div>
          <div className="text-sm text-gray-500">
            support@spirittech.io | spirittech.io
          </div>
        </div>
        <div className="text-right">
          <div className="mb-2">
            <div className="text-sm text-gray-600">Invoice ID</div>
            <div className="text-lg font-bold text-gray-900">{invoice.invoice_id}</div>
          </div>
          <div className="mb-2">
            <div className="text-sm text-gray-600">Date</div>
            <div className="font-semibold text-gray-900">{invoice.date}</div>
          </div>
          <div className="mb-2">
            <div className="text-sm text-gray-600">Platform</div>
            <div className="font-semibold text-gray-900">{invoice.platform}</div>
          </div>
          {invoice.order_id && (
            <div>
              <div className="text-sm text-gray-600">Order Reference</div>
              <div className="font-semibold text-gray-900">{invoice.order_id}</div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Summary */}
      <div className="invoice-section mb-6 avoid-break">
        <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
          Transaction Summary
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-600 mb-1">Customer</div>
            <div className="font-semibold text-gray-900">{invoice.customer}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Source Platform</div>
            <div className="font-semibold text-gray-900">
              {invoice.platform}
              {invoice.source && invoice.source !== invoice.platform && (
                <span className="text-sm text-gray-500"> ({invoice.source})</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items (What Was Sold) */}
      <div className="invoice-section mb-6 avoid-break">
        <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
          Line Items
        </h2>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="text-left py-2 text-sm text-gray-600 font-semibold">Description</th>
              <th className="text-right py-2 text-sm text-gray-600 font-semibold w-24">Qty</th>
              <th className="text-right py-2 text-sm text-gray-600 font-semibold w-32">Unit Price</th>
              <th className="text-right py-2 text-sm text-gray-600 font-semibold w-32">Line Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.line_items.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="py-3 text-gray-900">{item.description || item.name}</td>
                <td className="py-3 text-right text-gray-900">{item.quantity || 1}</td>
                <td className="py-3 text-right text-gray-900">${item.amount.toFixed(2)}</td>
                <td className="py-3 text-right font-semibold text-gray-900">
                  ${((item.quantity || 1) * item.amount).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-400">
              <td colSpan="3" className="py-3 text-right font-semibold text-gray-900">Subtotal</td>
              <td className="py-3 text-right font-bold text-gray-900">${totalLineItems.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Platform Fees & Adjustments */}
      {invoice.fees && invoice.fees.length > 0 && (
        <div className="invoice-section mb-6 avoid-break">
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
            Platform Fees & Adjustments
          </h2>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-2 text-sm text-gray-600 font-semibold">Description</th>
                <th className="text-right py-2 text-sm text-gray-600 font-semibold w-32">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.fees.map((fee, idx) => (
                <tr key={idx} className="border-b border-gray-200">
                  <td className="py-3 text-gray-900">{fee.description || fee.type || fee.name}</td>
                  <td className="py-3 text-right text-red-600 font-semibold">
                    ${fee.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-400">
                <td className="py-3 text-right font-semibold text-gray-900">Total Fees</td>
                <td className="py-3 text-right font-bold text-red-600">${totalFees.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Shipping & Fulfillment (SCM) */}
      {shippingAmount > 0 && (
        <div className="invoice-section mb-6 avoid-break">
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
            Shipping & Fulfillment (SCM)
          </h2>
          <div className="flex justify-between items-center py-3">
            <div className="text-gray-900">
              {invoice.shipping?.description || 'Shipping Cost'}
            </div>
            <div className="font-semibold text-gray-900 text-lg">
              ${shippingAmount.toFixed(2)}
            </div>
          </div>
          <div className="text-xs text-gray-500 italic mt-2">
            Shipping costs are managed by the SCM system.
          </div>
        </div>
      )}

      {/* Sales Notes / Narrative */}
      {invoice.notes && (
        <div className="invoice-section mb-6 avoid-break">
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
            Sales Notes / Narrative
          </h2>
          <div className="text-gray-700 whitespace-pre-wrap">
            {invoice.notes}
          </div>
        </div>
      )}

      {/* Invoice Totals */}
      <div className="invoice-totals mt-8 pt-6 border-t-2 border-gray-800">
        <div className="space-y-2 max-w-md ml-auto">
          <div className="flex justify-between items-center py-1">
            <div className="text-gray-700">Subtotal</div>
            <div className="font-semibold text-gray-900">${totalLineItems.toFixed(2)}</div>
          </div>
          {totalFees > 0 && (
            <div className="flex justify-between items-center py-1">
              <div className="text-gray-700">Total Fees</div>
              <div className="font-semibold text-red-600">${totalFees.toFixed(2)}</div>
            </div>
          )}
          {shippingAmount > 0 && (
            <div className="flex justify-between items-center py-1">
              <div className="text-gray-700">Shipping</div>
              <div className="font-semibold text-gray-900">${shippingAmount.toFixed(2)}</div>
            </div>
          )}
          <div className="flex justify-between items-center py-3 border-t-2 border-gray-400 mt-2">
            <div className="text-xl font-bold text-gray-900">Invoice Total</div>
            <div className="text-2xl font-bold text-gray-900">${totalAmount.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-6 border-t border-gray-300 text-center text-xs text-gray-500">
        <p>This is a Sales artifact. Finance will consume approved artifacts independently.</p>
        <p className="mt-1">Spirit Technologies LLC • support@spirittech.io</p>
      </div>
    </div>
  );
}
